// Tree-shakeable imports from Lucide
import replaceElement from 'lucide/dist/esm/replaceElement';
import defaultAttributes from 'lucide/dist/esm/defaultAttributes';
import ArrowRight from 'lucide/dist/esm/icons/arrow-right';
import Award from 'lucide/dist/esm/icons/award';
import Bookmark from 'lucide/dist/esm/icons/bookmark';
import Menu from 'lucide/dist/esm/icons/menu';
import X from 'lucide/dist/esm/icons/x';
import Moon from 'lucide/dist/esm/icons/moon';
import Sun from 'lucide/dist/esm/icons/sun';
import BookOpen from 'lucide/dist/esm/icons/book-open';
import Calendar from 'lucide/dist/esm/icons/calendar';
import CalendarX from 'lucide/dist/esm/icons/calendar-x';
import Check from 'lucide/dist/esm/icons/check';
import ChevronDown from 'lucide/dist/esm/icons/chevron-down';
import ChevronLeft from 'lucide/dist/esm/icons/chevron-left';
import ChevronRight from 'lucide/dist/esm/icons/chevron-right';
import CirclePlay from 'lucide/dist/esm/icons/circle-play';
import Clock from 'lucide/dist/esm/icons/clock';
import Download from 'lucide/dist/esm/icons/download';
import File from 'lucide/dist/esm/icons/file';
import FileDown from 'lucide/dist/esm/icons/file-down';
import Folder from 'lucide/dist/esm/icons/folder';
import Flag from 'lucide/dist/esm/icons/flag';
import Lightbulb from 'lucide/dist/esm/icons/lightbulb';
import LoaderCircle from 'lucide/dist/esm/icons/loader-circle';
import Star from 'lucide/dist/esm/icons/star';
import HardDriveDownload from 'lucide/dist/esm/icons/hard-drive-download';
import Link2 from 'lucide/dist/esm/icons/link-2';
import Mail from 'lucide/dist/esm/icons/mail';
import MapPin from 'lucide/dist/esm/icons/map-pin';
import PhoneIncoming from 'lucide/dist/esm/icons/phone-incoming';
import Phone from 'lucide/dist/esm/icons/phone';
import Plus from 'lucide/dist/esm/icons/plus';
import QrCode from 'lucide/dist/esm/icons/qr-code';
import RefreshCcw from 'lucide/dist/esm/icons/refresh-ccw';
import ScanBarcode from 'lucide/dist/esm/icons/scan-barcode';
import Search from 'lucide/dist/esm/icons/search';
import Settings from 'lucide/dist/esm/icons/settings';
import SlidersHorizontal from 'lucide/dist/esm/icons/sliders-horizontal';
import Users from 'lucide/dist/esm/icons/users';
import Upload from 'lucide/dist/esm/icons/upload';
import ZoomIn from 'lucide/dist/esm/icons/zoom-in';
import TriangleAlert from 'lucide/dist/esm/icons/triangle-alert';

import * as customIcons from './custom-icons';

const lucideIcons = {
	ArrowRight,
	Award,
	Bookmark,
	BookOpen,
	Calendar,
	CalendarX,
	Check,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	CirclePlay,
	Clock,
	Download,
	File,
	FileDown,
	Folder,
	Flag,
	HardDriveDownload,
	Link2,
	Lightbulb,
	LoaderCircle,
	Mail,
	MapPin,
	Menu,
	Moon,
	Phone,
	PhoneIncoming,
	Plus,
	QrCode,
	RefreshCcw,
	ScanBarcode,
	Search,
	Star,
	Settings,
	Sun,
	SlidersHorizontal,
	Upload,
	Users,
	X,
	ZoomIn,
	TriangleAlert
};

// Helper to create SVG elements
const createSVGElement = ([tag, attrs, children]) => {
	const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
	Object.keys(attrs).forEach((name) => {
		element.setAttribute(name, String(attrs[name]));
	});
	if (children?.length) {
		children.forEach((child) => {
			const childElement = createSVGElement(child);
			element.appendChild(childElement);
		});
	}
	return element;
};

// Custom createElement that handles custom icon attributes
const createElement = (iconNode, customAttrs = {}) => {
	let iconChildren = iconNode;
	let iconAttrs = {};
	let isCustomIcon = false;

	// Check if icon has custom attributes (for custom icons with viewBox)
	// Format: [[children], {viewBox, width, height}]
	if (
		Array.isArray(iconNode) &&
		iconNode.length === 2 &&
		Array.isArray(iconNode[0]) &&
		typeof iconNode[1] === 'object' &&
		!Array.isArray(iconNode[1])
	) {
		iconChildren = iconNode[0];
		iconAttrs = iconNode[1];
		isCustomIcon = true;
	}

	const tag = 'svg';

	// For custom icons with hardcoded fills, don't use Lucide's stroke defaults
	let baseAttrs = defaultAttributes;
	if (isCustomIcon) {
		// Custom icons: use fill instead of stroke
		baseAttrs = {
			xmlns: defaultAttributes.xmlns,
			width: defaultAttributes.width,
			height: defaultAttributes.height,
			viewBox: defaultAttributes.viewBox,
			fill: 'currentColor'
		};
	}

	const attrs = {
		...baseAttrs,
		...iconAttrs, // Custom icon attributes (viewBox, etc.)
		...customAttrs // User-provided attributes
	};
	return createSVGElement([tag, attrs, iconChildren]);
};

// Custom replaceElement that uses our createElement
const customReplaceElement = (element, {nameAttr, icons, attrs}) => {
	const iconName = element.getAttribute(nameAttr);
	if (iconName == null) return;

	// Convert kebab-case to PascalCase
	const ComponentName = iconName.replace(/(\w)(\w*)(_|-|\s*)/g, (g0, g1, g2) =>
		g1.toUpperCase() + g2.toLowerCase()
	);

	const iconNode = icons[ComponentName];
	if (!iconNode) {
		return console.warn(
			`${element.outerHTML} icon name was not found in the provided icons object.`
		);
	}

	const elementAttrs = Array.from(element.attributes).reduce((attrs, attr) => {
		attrs[attr.name] = attr.value;
		return attrs;
	}, {});

	const iconAttrs = {
		'data-lucide': iconName,
		...attrs,
		...elementAttrs
	};

	const classNames = [
		'lucide',
		`lucide-${iconName}`,
		elementAttrs.class,
		attrs.class
	]
		.filter(Boolean)
		.join(' ');

	if (classNames) {
		iconAttrs.class = classNames;
	}

	const svgElement = createElement(iconNode, iconAttrs);
	return element.parentNode?.replaceChild(svgElement, element);
};

/**
 * Minimal createIcons implementation for tree-shaking
 * Based on lucide v0.546.0 source code
 * This avoids importing the entire icon library (1500+ icons)
 */
function createIcons({
	icons = {},
	nameAttr = 'data-lucide',
	attrs = {},
	root = document
} = {}) {
	if (!Object.values(icons).length) {
		throw new Error(
			'Please provide an icons object.\nIf you want to use all the icons you can import it like:\n `import { createIcons, icons } from \'lucide\';\nlucide.createIcons({icons});`'
		);
	}
	if (typeof root === 'undefined') {
		throw new Error('`createIcons()` only works in a browser environment.');
	}

	const elementsToReplace = root.querySelectorAll(`[${nameAttr}]`);

	Array.from(elementsToReplace).forEach((element) =>
		customReplaceElement(element, {nameAttr, icons, attrs})
	);
}

export function initLucide() {
	createIcons({
		attrs: {'stroke-width': 1.5},
		icons: {
			...lucideIcons,
			...customIcons
		}
	});
}

// Expose globally for use in inline scripts
if (typeof window !== 'undefined') {
	window.initLucide = initLucide;
}
